import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  cleanupNotificationRegistration,
  getNotificationCapability,
  startNotificationRegistration,
} from '../services/notificationService';
import { AuthContext } from './AuthContext';

export type NotificationPreference = 'unset' | 'enabled' | 'disabled';
export type NotificationStatus =
  | 'loading'
  | 'ready'
  | 'registering'
  | 'unregistering'
  | 'enabled'
  | 'disabled'
  | 'blocked'
  | 'unsupported'
  | 'registration-error'
  | 'push-service-unavailable'
  | 'ios-install-required';

interface NotificationContextValue {
  preference: NotificationPreference;
  promptDismissed: boolean;
  status: NotificationStatus;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  dismiss: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

const PROMPT_DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000;

const getPreferenceStorageKey = (userId: string) =>
  `notifications.preference:${userId}`;

const getPromptDismissalStorageKey = (userId: string) =>
  `notifications.promptDismissedAt:${userId}`;

const readPreference = (userId: string): NotificationPreference => {
  try {
    const value = window.localStorage.getItem(getPreferenceStorageKey(userId));
    return value === 'enabled' || value === 'disabled' ? value : 'unset';
  } catch (error) {
    console.error('[Notifications] Failed to read preference', error);
    return 'unset';
  }
};

const persistPreference = (
  userId: string,
  preference: NotificationPreference,
) => {
  try {
    window.localStorage.setItem(getPreferenceStorageKey(userId), preference);
  } catch (error) {
    console.error('[Notifications] Failed to persist preference', error);
  }
};

const readPromptDismissed = (userId: string) => {
  try {
    const dismissedAt = Number(
      window.localStorage.getItem(getPromptDismissalStorageKey(userId)),
    );
    return (
      Number.isFinite(dismissedAt) &&
      dismissedAt > 0 &&
      Date.now() - dismissedAt < PROMPT_DISMISSAL_TTL_MS
    );
  } catch (error) {
    console.error('[Notifications] Failed to read the prompt dismissal', error);
    return false;
  }
};

const persistPromptDismissal = (userId: string) => {
  try {
    window.localStorage.setItem(
      getPromptDismissalStorageKey(userId),
      String(Date.now()),
    );
  } catch (error) {
    console.error(
      '[Notifications] Failed to persist the prompt dismissal',
      error,
    );
  }
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { firebaseUser } = useContext(AuthContext);
  const userId = firebaseUser?.uid;
  const [preference, setPreference] = useState<NotificationPreference>('unset');
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [status, setStatus] = useState<NotificationStatus>('loading');
  const registrationAttemptRef = useRef(0);

  const updatePreference = useCallback(
    (nextPreference: NotificationPreference) => {
      setPreference(nextPreference);
      if (userId) {
        persistPreference(userId, nextPreference);
      }
    },
    [userId],
  );

  const registerBrowser = useCallback(async () => {
    if (!userId) {
      return;
    }

    const attempt = registrationAttemptRef.current + 1;
    registrationAttemptRef.current = attempt;
    setStatus('registering');
    try {
      await startNotificationRegistration(userId, {
        onRegistered: () => {
          if (registrationAttemptRef.current !== attempt) {
            return;
          }

          updatePreference('enabled');
          setStatus('enabled');
        },
        onRegistrationError: (error) => {
          if (registrationAttemptRef.current !== attempt) {
            return;
          }

          console.error('[Notifications] Registration sync failed', error);
          setStatus('registration-error');
        },
      });
    } catch (error) {
      if (registrationAttemptRef.current !== attempt) {
        return;
      }

      console.error('[Notifications] Browser registration failed', error);
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setStatus('registration-error');
        return;
      }

      setStatus('push-service-unavailable');
    }
  }, [updatePreference, userId]);

  useEffect(() => {
    let cancelled = false;

    if (!userId) {
      setPreference('unset');
      setPromptDismissed(false);
      setStatus('loading');
      return;
    }

    const storedPreference = readPreference(userId);
    setPreference(storedPreference);
    setPromptDismissed(readPromptDismissed(userId));

    void getNotificationCapability()
      .then((capability) => {
        if (cancelled) {
          return;
        }

        if (capability !== 'ready') {
          setStatus(capability);
          return;
        }

        if (storedPreference === 'disabled') {
          setStatus('disabled');
          return;
        }

        if (storedPreference === 'enabled') {
          if (Notification.permission === 'granted') {
            void registerBrowser();
            return;
          }

          updatePreference('unset');
        }

        setStatus('ready');
      })
      .catch((error) => {
        console.error('[Notifications] Capability check failed', error);
        if (!cancelled) {
          setStatus('registration-error');
        }
      });

    return () => {
      cancelled = true;
      registrationAttemptRef.current += 1;
    };
  }, [registerBrowser, updatePreference, userId]);

  const enable = useCallback(async () => {
    if (!userId) {
      return;
    }

    const capability = await getNotificationCapability();
    if (capability !== 'ready') {
      setStatus(capability);
      return;
    }

    const permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();

    if (permission !== 'granted') {
      setStatus('blocked');
      return;
    }

    await registerBrowser();
  }, [registerBrowser, userId]);

  const disable = useCallback(async () => {
    if (!userId) {
      return;
    }

    registrationAttemptRef.current += 1;
    updatePreference('disabled');
    setStatus('unregistering');
    await cleanupNotificationRegistration(userId);
    setStatus('disabled');
  }, [updatePreference, userId]);

  const dismiss = useCallback(() => {
    if (!userId) {
      return;
    }

    persistPromptDismissal(userId);
    setPromptDismissed(true);
  }, [userId]);

  const value = useMemo(
    () => ({ preference, promptDismissed, status, enable, disable, dismiss }),
    [disable, dismiss, enable, preference, promptDismissed, status],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within NotificationProvider',
    );
  }

  return context;
}
